import { motion } from "framer-motion";
import { Building, Calendar, Award, TrendingUp, Users, Globe, Shield, Zap } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";

// Component displaying VIDAVO company timeline and milestones
const Vidavo = () => {
  const { t } = useTranslation();
  const timelineEvents = [{
    year: "2025",
    titleKey: "event2025" as const,
    icon: Globe,
    solidColor: "#A9E0DD",
    shadowColor: "shadow-[#A9E0DD]/20"
  }, {
    year: "2024",
    titleKey: "event2024" as const,
    icon: Shield,
    solidColor: "#66B3BB",
    shadowColor: "shadow-[#66B3BB]/20"
  }, {
    year: "2023",
    titleKey: "event2023" as const,
    icon: Zap,
    solidColor: "#C8DDFF",
    shadowColor: "shadow-[#C8DDFF]/20"
  }, {
    year: "2021",
    titleKey: "event2021" as const,
    icon: Users,
    solidColor: "#868EAD",
    shadowColor: "shadow-[#868EAD]/20"
  }, {
    year: "2020",
    titleKey: "event2020" as const,
    icon: Award,
    solidColor: "#B4C3FD",
    shadowColor: "shadow-[#B4C3FD]/20"
  }, {
  year: "2019",
    titleKey: "event2019" as const,
    icon: Globe,
    solidColor: "#70989c",
    shadowColor: "shadow-[#70989c]/20"
  }, {
    year: "2015",
    titleKey: "event2015" as const,
    icon: Building,
    solidColor: "#A9E0DD",
    shadowColor: "shadow-[#A9E0DD]/20"
  }, {
    year: "2014",
    titleKey: "event2014" as const,
    icon: Shield,
    solidColor: "#66B3BB",
    shadowColor: "shadow-[#66B3BB]/20"
  }, {
    year: "2011",
    titleKey: "event2011" as const,
    icon: Zap,
    solidColor: "#C8DDFF",
    shadowColor: "shadow-[#C8DDFF]/20"
  }, {
    year: "2010",
    titleKey: "event2010" as const,
    icon: TrendingUp,
    solidColor: "#868EAD",
    shadowColor: "shadow-[#868EAD]/20"
  }, {
    year: "2007",
    titleKey: "event2007" as const,
    icon: Building,
    solidColor: "#B4C3FD",
    shadowColor: "shadow-[#B4C3FD]/20"
  }, {
  year: "2005",
    titleKey: "event2005" as const,
    icon: TrendingUp,
    solidColor: "#70989c",
    shadowColor: "shadow-[#70989c]/20"
  }, {
    year: "2002",
    titleKey: "event2002" as const,
    icon: Building,
    solidColor: "#A9E0DD",
    shadowColor: "shadow-[#A9E0DD]/20"
  }];
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      <main className="pt-32 pb-16 relative overflow-hidden">
        
        
        <div className="container-padding relative z-10">
          <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <motion.div className="text-center mb-20" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }}>
              <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 
                           border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium
                           backdrop-blur-sm mb-6" initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
                <Calendar className="w-4 h-4" />
                {t.vidavo.badge}
              </motion.div>
              
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.3
            }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-8 py-[20px] text-accent-teal font-bold">
                {t.vidavo.title}
              </motion.h1>
              
              <motion.p className="text-base sm:text-lg md:text-xl text-neutral-700 max-w-3xl mx-auto leading-relaxed px-4" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.4
            }}>
                {t.vidavo.subtitle}
              </motion.p>
            </motion.div>

            {/* Timeline */}
            <div className="relative max-w-6xl mx-auto">
              {/* Central timeline line */}
              <div className="absolute left-1/2 top-0 bottom-0 transform -translate-x-1/2 z-0">
                <div className="relative w-1 h-full">
                  {/* Base line */}
                  <div className="absolute inset-0 w-px bg-gradient-to-b from-transparent via-[#00808D]/60 to-transparent ml-0.5"></div>
                </div>
              </div>
              
              {/* Timeline events */}
              <div className="space-y-16">
                {timelineEvents.map((event, index) => <motion.div key={event.year} className="relative z-10" initial={{
                opacity: 0,
                y: 50
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.8,
                delay: index * 0.15
              }}>
                    
                    {/* Content card */}
                    <div className={`flex ${index % 2 === 0 ? 'justify-end pr-4 sm:pr-12 md:pr-24' : 'justify-start pl-4 sm:pl-12 md:pl-24'}`}>
                      <motion.div className="w-full max-w-md group cursor-pointer" whileHover={{
                    scale: 1.02,
                    y: -4
                  }} transition={{
                    duration: 0.3
                  }}>
                        {/* Year badge */}
                        <div className="inline-block mb-4 px-4 py-2 rounded-full text-black font-bold text-sm shadow-lg" style={{
                      backgroundColor: event.solidColor
                    }}>
                          {event.year}
                        </div>
                        
                        {/* Card */}
                        <div className="relative overflow-hidden rounded-2xl border border-white/20 shadow-xl backdrop-blur-sm group-hover:shadow-2xl transition-all duration-300 min-w-0" style={{
                      backgroundColor: event.solidColor + '15'
                    }}>
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 opacity-10" style={{
                        background: `linear-gradient(135deg, ${event.solidColor}40, ${event.solidColor}20)`
                      }} />
                          
                          <div className="relative p-4 sm:p-6 md:p-8">
                            {/* Icon */}
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl mb-4 sm:mb-6 flex items-center justify-center shadow-lg transform rotate-3 group-hover:rotate-0 transition-transform duration-300 flex-shrink-0" style={{
                          backgroundColor: event.solidColor + '20'
                        }}>
                              <event.icon className="w-6 h-6 sm:w-7 sm:h-7" style={{
                            color: event.solidColor
                          }} />
                            </div>
                            
                            {/* Content */}
                            <h3 className="text-base sm:text-lg md:text-xl font-bold mb-3 leading-tight text-slate-800 break-words hyphens-auto" style={{ wordBreak: 'break-word' }}>
                              {t.vidavo.timeline[event.titleKey]}
                            </h3>
                          </div>
                          
                          {/* Bottom accent line */}
                          <div className="absolute bottom-0 left-0 right-0 h-1" style={{
                        backgroundColor: event.solidColor
                      }} />
                         </div>
                      </motion.div>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Bottom section */}
            <motion.div className="text-center mt-20 healthcare-card relative" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 1.5
          }}>
              <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent-teal/5 rounded-2xl" />
              <div className="relative z-10">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-secondary to-accent-teal text-white">
                  <Award className="w-8 h-8" />
                </div>
                <h2 className="text-3xl font-bold text-primary">{t.vidavo.buildingFuture.title}</h2>
              </div>
              <p className="text-lg text-neutral-700 leading-relaxed max-w-3xl mx-auto">
                {t.vidavo.buildingFuture.description}
              </p>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default Vidavo;